// @ts-strict-ignore
import { useState } from 'react';
import { throttle } from 'lodash';
import Button from 'src/components/buttons/button';
import { setAlert } from 'src/components/alert/actions';
import { showSignupModal } from 'src/modules/shared/modal/actions';
import { userHasPermission, useIsCommunityMember } from 'src/utils/context';
import { useDispatch, useSelector } from 'src/store/store';
import { updatePostLike } from 'src/modules/community/singlePost/actions/actions';
import i18n from 'src/utils/translate';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, FootfallContextType, Element, Section } from 'src/constants/footfall';
import { Permission, Post, Reply } from 'src/constants/types';

type Props = {
  post: Post | Reply;
  signupContext?: FootfallContextType;
  noText?: boolean;
}

export default function LikeButton({
  post,
  signupContext = {
    element: Element.LIKE,
    section: Section.UNTRACKED,
  },
  noText = false,
}: Props): JSX.Element {
  const dispatch = useDispatch();

  const { currentUser, currentCommunity } = useSelector((state) => state.context);
  const { numRatings, hasRated } = post;
  const isActive = !!currentUser;
  const isDisabled = !!currentUser && post.author.id === currentUser?.userId;

  const [isHovered, setIsHovered] = useState(false);

  const isCommunityMember = useIsCommunityMember();

  const onClick = () => {
    if (isDisabled) return;

    if (!isActive) {
      trackFootfallEvent(Event.CLICKED, {
        clickType: 'sign-up',
        clickMetadata: {
          section: signupContext.section || Section.UNTRACKED,
          element: signupContext.element || Element.LIKE,
        },
      });
      dispatch(showSignupModal({ community: currentCommunity }));
      return;
    }

    if (isActive && !isCommunityMember) {
      dispatch(setAlert(i18n.t(`You'll need to become a community member to be able to Like.`)));
      return;
    }

    if (!userHasPermission(currentUser, Permission.RateItem)) {
      dispatch(setAlert(i18n.t(`You have been restricted in this community. Please contact this community's administrator to find out why.`)));
      return;
    }

    dispatch(updatePostLike(post.id, currentCommunity.slug));
  };

  const throttledClick = throttle(onClick, 1000);

  let likesNumber = '';

  likesNumber = `(${numRatings || 0})`;
  if (noText && !!numRatings && numRatings.toString() === '0') likesNumber = '';

  const buttonText = hasRated && isHovered
    ? i18n.t('Unlike')
    : `${i18n.t('Like')} ${likesNumber}`;

  const iconName = isDisabled || (!hasRated && !isHovered)
    ? 'like-outline'
    : 'like-filled';

  return (
    <Button
      dataTestId="like-button"
      disabled={isDisabled}
      icon={iconName}
      size="sm"
      text={noText ? likesNumber : buttonText}
      type={isDisabled ? 'tertiary' : 'secondary'}
      onClick={isActive ? throttledClick : onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    />
  );
}
